import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { Spinner } from 'react-bootstrap';

import ImagePreviewModal from '@/components/media/ImagePreviewModal.js';
import {
  createJianyingImageValue,
} from '@/features/automation/jianying/utils/jianyingSlots.js';
import JianyingImageCard from './JianyingImageCard.js';
import JianyingManualCropModal from './JianyingManualCropModal.js';

const collectSlotNodes = (nodes) => nodes.flatMap((node) => (
  node.type === 'group' ? collectSlotNodes(node.children) : [node]
));

const findAncestorGroupKeys = (nodes, targetKey, ancestors = []) => {
  for (const node of nodes) {
    if (node.type === 'slot' && node.key === targetKey) return ancestors;
    if (node.type === 'group') {
      const found = findAncestorGroupKeys(node.children, targetKey, [...ancestors, node.key]);
      if (found) return found;
    }
  }
  return null;
};

const ImageAssignmentGrid = ({
  disabled,
  errors = {},
  images,
  onChange,
  onCopy,
  onCrop,
  onPreview,
  onReorder,
  onSmartCrop,
  registerFieldRef,
  slots,
  smartCropDisabled,
  values = {},
}) => {
  const [suppressDropAnimation, setSuppressDropAnimation] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  }));
  const selectedAssignments = slots.flatMap((slot) => {
    const value = values[slot.id];
    if (!value?.image_id) return [];
    return [{
      slot,
      value,
      image: images.find((image) => String(image.id) === String(value.image_id)),
    }];
  });
  const selectedImageIds = new Set(
    selectedAssignments.map(({ value }) => String(value.image_id)),
  );
  const availableImages = images.filter((image) => !selectedImageIds.has(String(image.id)));
  const isFull = selectedAssignments.length >= slots.length;
  const errorMessages = slots.flatMap((slot) => (errors[slot.id] ? [errors[slot.id]] : []));

  const selectImage = (image, source) => {
    const emptySlot = slots.find((slot) => !values[slot.id]?.image_id);
    if (!emptySlot) return;
    onChange(emptySlot.id, createJianyingImageValue(image, source));
  };

  const copyImage = (value) => {
    const emptySlot = slots.find((slot) => !values[slot.id]?.image_id);
    if (!emptySlot) return;
    onCopy(emptySlot.id, value);
  };

  useEffect(() => {
    if (!suppressDropAnimation) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      setSuppressDropAnimation(false);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [suppressDropAnimation]);

  const suppressNextDropAnimation = () => {
    setSuppressDropAnimation(true);
  };

  const handleDragEnd = ({ active, over }) => {
    suppressNextDropAnimation();
    if (!over || active.id === over.id) return;
    const oldIndex = selectedAssignments.findIndex(({ slot }) => slot.id === active.id);
    const newIndex = selectedAssignments.findIndex(({ slot }) => slot.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(selectedAssignments, oldIndex, newIndex);
    selectedAssignments.forEach(({ slot }, index) => {
      onReorder(slot.id, reordered[index].value);
    });
  };

  return (
    <div className="jianying-image-assignment">
      <div className="jianying-image-assignment__summary">
        已选 {selectedAssignments.length} / {slots.length} · 按点击顺序分配，可拖动已选图片调整顺序
      </div>
      {slots.map((slot) => (
        <span
          key={slot.id}
          ref={(node) => registerFieldRef(slot.id, node)}
          className="jianying-image-assignment__anchor"
          tabIndex={-1}
        />
      ))}
      {errorMessages.length ? (
        <div className="jianying-image-assignment__errors" role="alert">
          {errorMessages.map((message) => <p key={message}>{message}</p>)}
        </div>
      ) : null}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragCancel={suppressNextDropAnimation}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={selectedAssignments.map(({ slot }) => slot.id)}
          strategy={rectSortingStrategy}
        >
          <div className="jianying-group-image-grid">
            {selectedAssignments.map(({ slot, value, image }, index) => (
              <JianyingImageCard
                key={slot.id}
                assignmentLabel={slot.name}
                disabled={disabled}
                entry={{
                  id: slot.id,
                  sortId: slot.id,
                  state: 'selected',
                  item: value,
                  image,
                }}
                index={index}
                isSelected
                copyDisabled={isFull}
                onCopy={() => copyImage(value)}
                onCrop={() => onCrop(slot.id)}
                onPreview={onPreview}
                onRemove={() => onChange(slot.id, null)}
                onSmartCrop={onSmartCrop}
                onSourceChange={(source) => onChange(slot.id, { ...value, source })}
                showSequenceControls
                smartCropDisabled={smartCropDisabled}
                suppressDropAnimation={suppressDropAnimation}
                sortable
              />
            ))}
            {availableImages.map((image, index) => (
              <JianyingImageCard
                key={image.id}
                disabled={disabled || isFull}
                entry={{
                  id: `available-${image.id}`,
                  state: isFull ? 'unavailable' : 'unselected',
                  item: null,
                  image,
                }}
                index={index}
                onPreview={onPreview}
                onSelect={selectImage}
                onSmartCrop={onSmartCrop}
                smartCropDisabled={smartCropDisabled}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {!images.length ? <div className="jianying-empty">该订单没有可用图片</div> : null}
    </div>
  );
};

const ImageGroup = ({
  collapsedKeys,
  depth,
  errors = {},
  node,
  setCollapsedKeys,
  values = {},
  ...assignmentProps
}) => {
  const isCollapsed = collapsedKeys.has(node.key);
  const slotCount = collectSlotNodes(node.children).length;
  const directSlots = node.children
    .filter((child) => child.type === 'slot')
    .map((child) => child.slot);
  const childGroups = node.children.filter((child) => child.type === 'group');
  const contentId = `jianying-image-group-${node.key}`;

  return (
    <section className="jianying-business-group" style={{ '--jy-group-depth': depth }}>
      <button
        type="button"
        className="jianying-business-group__header"
        onClick={() => setCollapsedKeys((current) => {
          const next = new Set(current);
          if (next.has(node.key)) next.delete(node.key); else next.add(node.key);
          return next;
        })}
        aria-expanded={!isCollapsed}
        aria-controls={contentId}
      >
        <i className={`bi bi-chevron-${isCollapsed ? 'right' : 'down'}`} />
        <i className="bi bi-collection-play" />
        <span><strong>{node.group.name}</strong><small>{slotCount} 个图片槽位</small></span>
      </button>
      {!isCollapsed ? (
        <div id={contentId} className="jianying-business-group__children">
          {directSlots.length ? (
            <ImageAssignmentGrid
              {...assignmentProps}
              errors={errors}
              slots={directSlots}
              values={values}
            />
          ) : null}
          {childGroups.map((child) => (
            <ImageGroup
              key={child.key}
              {...assignmentProps}
              collapsedKeys={collapsedKeys}
              depth={depth + 1}
              errors={errors}
              node={child}
              setCollapsedKeys={setCollapsedKeys}
              values={values}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
};

const JianyingImageMaterialPanel = ({
  disabled = false,
  errors = {},
  hidden = false,
  images = [],
  onChange = () => {},
  onProcessAll = () => {},
  onSmartCrop = () => {},
  registerFieldRef = () => {},
  sections = [],
  smartCropStatus = { state: 'idle' },
  values = {},
}) => {
  const [cropSlotId, setCropSlotId] = useState('');
  const [previewTarget, setPreviewTarget] = useState(null);
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const manualUrls = useRef(new Set());
  const slots = useMemo(() => collectSlotNodes(sections).map((node) => node.slot), [sections]);
  const ungroupedSlots = useMemo(() => sections
    .filter((node) => node.type === 'slot')
    .map((node) => node.slot), [sections]);
  const cropSlot = slots.find((slot) => slot.id === cropSlotId);
  const cropItem = cropSlot ? values[cropSlot.id] : null;
  const cropImage = cropItem
    ? images.find((image) => String(image.id) === String(cropItem.image_id))
    : null;
  const smartCropProcessing = smartCropStatus.state === 'processing';

  useEffect(() => () => {
    manualUrls.current.forEach((url) => URL.revokeObjectURL(url));
    manualUrls.current.clear();
  }, []);

  useEffect(() => {
    const errorKeys = Object.keys(errors).filter((key) => errors[key]);
    if (!errorKeys.length) return;
    setCollapsedKeys((current) => {
      const next = new Set(current);
      errorKeys.forEach((key) => {
        (findAncestorGroupKeys(sections, key) || []).forEach((groupKey) => next.delete(groupKey));
      });
      return next;
    });
  }, [errors, sections]);

  const releaseManualPreview = (value) => {
    if (!value?.manualPreviewUrl) return;
    URL.revokeObjectURL(value.manualPreviewUrl);
    manualUrls.current.delete(value.manualPreviewUrl);
  };

  const changeValue = (slotId, value) => {
    releaseManualPreview(values[slotId]);
    onChange(slotId, value);
  };

  const copyValue = (slotId, value) => {
    let nextValue = { ...value };
    if (value?.manualBlob) {
      const manualPreviewUrl = URL.createObjectURL(value.manualBlob);
      manualUrls.current.add(manualPreviewUrl);
      nextValue = {
        ...nextValue,
        manualPreviewUrl,
        object_key: undefined,
      };
    }
    onChange(slotId, nextValue);
  };

  const reorderValue = (slotId, value) => {
    onChange(slotId, value);
  };

  const saveManualCrop = (blob) => {
    if (!cropSlot || !cropItem) return;
    releaseManualPreview(cropItem);
    const manualPreviewUrl = URL.createObjectURL(blob);
    manualUrls.current.add(manualPreviewUrl);
    onChange(cropSlot.id, {
      ...cropItem,
      source: 'manual',
      manualBlob: blob,
      manualPreviewUrl,
      object_key: undefined,
    });
    setCropSlotId('');
  };

  if (!sections.length) return null;

  const assignmentProps = {
    disabled,
    images,
    onChange: changeValue,
    onCopy: copyValue,
    onCrop: setCropSlotId,
    onPreview: setPreviewTarget,
    onReorder: reorderValue,
    onSmartCrop,
    registerFieldRef,
    smartCropDisabled: smartCropProcessing,
  };

  return (
    <section hidden={hidden} className="jianying-material-panel jianying-image-material-panel">
      <div className="jianying-material-panel__header">
        <span><i className="bi bi-images" /><strong>图片素材</strong><small>{slots.length} 个槽位</small></span>
        <button type="button" className="jianying-smart-crop-all" disabled={disabled || smartCropProcessing} onClick={onProcessAll}>
          {smartCropProcessing ? <Spinner size="sm" /> : <i className="bi bi-stars" />}
          {smartCropProcessing ? '自动裁切中…' : '自动裁切全部'}
        </button>
      </div>
      <div className="jianying-image-material-panel__content">
        {smartCropStatus.state === 'error' || Number(smartCropStatus.summary?.failed) > 0 ? (
          <div className={`jianying-smart-crop-status is-${smartCropStatus.state}`}>
            <span>{smartCropStatus.message || '部分图片处理失败，已保留原图'}</span>
          </div>
        ) : null}
        {ungroupedSlots.length ? (
          <section className="jianying-business-group">
            <div className="jianying-business-group__header is-static">
              <i className="bi bi-collection" />
              <span><strong>未分组素材</strong><small>{ungroupedSlots.length} 个图片槽位</small></span>
            </div>
            <div className="jianying-business-group__children">
              <ImageAssignmentGrid
                {...assignmentProps}
                errors={errors}
                slots={ungroupedSlots}
                values={values}
              />
            </div>
          </section>
        ) : null}
        {sections.map((node) => node.type === 'group' ? (
          <ImageGroup
            key={node.key}
            {...assignmentProps}
            collapsedKeys={collapsedKeys}
            depth={0}
            errors={errors}
            node={node}
            setCollapsedKeys={setCollapsedKeys}
            values={values}
          />
        ) : null)}
      </div>
      <JianyingManualCropModal show={Boolean(cropSlot && cropItem)} image={cropImage} onCancel={() => setCropSlotId('')} onSave={saveManualCrop} />
      <ImagePreviewModal show={Boolean(previewTarget?.src)} src={previewTarget?.src || ''} alt={previewTarget?.alt || ''} title={previewTarget?.title || '图片预览'} onClose={() => setPreviewTarget(null)} />
    </section>
  );
};

export default JianyingImageMaterialPanel;
